import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import { Button, ButtonGroup, Grow, MenuList, Paper, Popper, type ButtonGroupProps } from "@mui/material";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import type { JSX } from "react";
import { useState } from "react";
import ActionMenuItem from "./ActionMenuItem";

interface Props {
  primaryOption: SplitButtonOption;
  secondaryOptions: SplitButtonOption[];
  saving?: boolean;
  disabled?: boolean;
  primaryActionStartIcon?: React.ReactNode;
  variant?: ButtonGroupProps["variant"];
  color?: ButtonGroupProps["color"];
}

interface SplitButtonOption {
  title: string | JSX.Element;
  icon?: React.ElementType;
  handler: () => void;
  disabled?: boolean;
}

const SplitButton = ({
  primaryOption,
  secondaryOptions,
  saving,
  disabled,
  primaryActionStartIcon,
  variant,
  color,
}: Props) => {
  const [open, setOpen] = useState(false);

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleClose = (event: Event) => {
    if (anchorEl && anchorEl.contains(event.target as HTMLElement)) {
      return;
    }

    setOpen(false);
  };

  return (
    <>
      <ButtonGroup ref={setAnchorEl} variant={variant ?? "contained"} disableElevation color={color}>
        <Button
          startIcon={primaryActionStartIcon}
          onClick={primaryOption.handler}
          loading={saving}
          disabled={saving || disabled}
          sx={{ minWidth: "auto" }}
        >
          {primaryOption.title}
        </Button>
        {secondaryOptions.length > 0 && (
          <Button
            disabled={saving || disabled}
            aria-controls={open ? "split-button-menu" : undefined}
            aria-expanded={open ? "true" : undefined}
            aria-label="select secondary option"
            aria-haspopup="menu"
            onClick={() => setOpen((prevOpen) => !prevOpen)}
            sx={{ minWidth: "unset", p: 0.75 }}
          >
            <ExpandMoreRoundedIcon color="inherit" />
          </Button>
        )}
      </ButtonGroup>

      <Popper
        sx={{
          zIndex: 1400,
          minWidth: anchorEl?.clientWidth || 126,
        }}
        open={open}
        anchorEl={anchorEl}
        transition
        placement="bottom-end"
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps}>
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList autoFocusItem>
                  {secondaryOptions.map((option, i) => (
                    <ActionMenuItem
                      // eslint-disable-next-line @eslint-react/no-array-index-key
                      key={i}
                      title={option.title}
                      icon={option.icon ? <option.icon fontSize="small" /> : <></>}
                      onClick={() => {
                        setOpen(false);
                        secondaryOptions[i]?.handler();
                      }}
                      disabled={option.disabled}
                    />
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
};

export default SplitButton;
